import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/Button/Button';
import { AuthCard } from '@/features/auth';
import { resendVerification } from '@/features/auth/api';
import { ApiError } from '@/lib/api';
import { t } from '@/lib/i18n';

const RESEND_COOLDOWN_S = 60;

export default function VerifyEmailSentRoute() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state } = useLocation() as { state?: { email?: string } };
  const success = searchParams.get('success') === '1';
  const email = state?.email ?? null;

  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  async function handleResend() {
    if (!email || cooldown > 0) return;
    setStatus('sending');
    try {
      await resendVerification(email);
      setStatus('sent');
      setCooldown(RESEND_COOLDOWN_S);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setStatus('idle');
        setCooldown(RESEND_COOLDOWN_S);
      } else {
        setStatus('error');
      }
    }
  }

  if (success) {
    return (
      <AuthCard title={t("You're in.")} tagline={t('Build a bigger team. Stay your size.')}>
        <p style={{ fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
          {t('Your account is ready. Sign in to meet your team.')}
        </p>
        <div style={{ marginTop: 20 }}>
          <Button variant="primary" size="lg" onClick={() => void navigate('/login')}>
            {t('Sign in →')}
          </Button>
        </div>
      </AuthCard>
    );
  }

  if (!email) {
    return (
      <AuthCard title={t('Check your inbox')} tagline={t('Build a bigger team. Stay your size.')}>
        <p style={{ fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
          {t('If you recently signed up, check your inbox for a confirmation link.')}
        </p>
      </AuthCard>
    );
  }

  return (
    <AuthCard title={t('Check your inbox')} tagline={t('Build a bigger team. Stay your size.')}>
      <p style={{ fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
        {t("We've sent a confirmation link to")} <strong>{email}</strong>
        {'. '}
        {t('Click it to verify your account and sign in.')}
      </p>

      <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {status === 'sent' ? (
          <p style={{ fontSize: 13, color: 'var(--color-success)', textAlign: 'center' }}>
            {t('Sent!')}
          </p>
        ) : (
          <Button
            variant="secondary"
            size="md"
            isLoading={status === 'sending'}
            disabled={cooldown > 0}
            onClick={() => void handleResend()}
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : t('Resend email')}
          </Button>
        )}
        {status === 'error' ? (
          <p style={{ fontSize: 13, color: 'var(--color-danger)', textAlign: 'center' }}>
            {t('Something went wrong. Please try again.')}
          </p>
        ) : null}
        <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--color-text-muted)' }}>
          <Link to="/signup">{t('Wrong email? Start over →')}</Link>
        </p>
      </div>
    </AuthCard>
  );
}
